import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { PERMISSIONS } from '../utils/permissions';
import { apiCall } from '../services/realApi';
import type { Client } from '../types';

interface ClientContextValue {
  selectedClientId: string | null;
  accessibleClients: Client[];
  isLoadingClients: boolean;
  error: string | null;
  selectClient: (clientId: string | null) => void;
  clearClient: () => void;
  loadAccessibleClients: () => Promise<void>;
  isAWSUser: boolean;
  isAdminUser: boolean;
  hasClientAccess: boolean;
}

const CLIENT_STORAGE_KEY = 'selectedClientId';

const ClientContext = createContext<ClientContextValue | null>(null);

export const useClient = (): ClientContextValue => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(() => {
    if (typeof globalThis.window !== 'undefined') {
      const stored = localStorage.getItem(CLIENT_STORAGE_KEY);
      return stored || null;
    }
    return null;
  });
  const [accessibleClients, setAccessibleClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasClientAccess = useMemo(() => {
    return (
      isAWSUser() ||
      isAdmin ||
      hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
      hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
      hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_MANAGE)
    );
  }, [hasPermission, isAdmin, isAWSUser]);

  const loadAccessibleClients = useCallback(async () => {
    if (!hasClientAccess) {
      return;
    }

    setIsLoadingClients(true);
    setError(null);
    try {
      const endpoint = isAWSUser()
        ? '/aws-users/me/accessible-clients'
        : '/clients';
      const data = await apiCall(endpoint);
      const clients: Client[] = Array.isArray(data) ? data : [];
      setAccessibleClients(clients);

      const storedClientId = localStorage.getItem(CLIENT_STORAGE_KEY);
      if (storedClientId) {
        const isValid = clients.some(c => c.id === storedClientId);
        if (isValid) {
          setSelectedClientId(storedClientId);
        } else {
          localStorage.removeItem(CLIENT_STORAGE_KEY);
          setSelectedClientId(null);
        }
      }
    } catch (err: unknown) {
      console.error('Error loading accessible clients:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accessible clients');
      setAccessibleClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  }, [hasClientAccess, isAWSUser]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.role === 'client') {
      setSelectedClientId(null);
      setAccessibleClients([]);
      localStorage.removeItem(CLIENT_STORAGE_KEY);
      return;
    }

    if (hasClientAccess) {
      loadAccessibleClients();
    }
  }, [user, hasClientAccess, loadAccessibleClients]);

  useEffect(() => {
    if (hasClientAccess && accessibleClients.length > 0 && !selectedClientId) {
      const firstClient = accessibleClients[0];
      setSelectedClientId(firstClient.id);
      localStorage.setItem(CLIENT_STORAGE_KEY, firstClient.id);
    }
  }, [accessibleClients, hasClientAccess, selectedClientId]);

  const selectClient = useCallback((clientId: string | null) => {
    if (clientId) {
      setSelectedClientId(clientId);
      localStorage.setItem(CLIENT_STORAGE_KEY, clientId);
      globalThis.location.reload();
    } else {
      setSelectedClientId(null);
      localStorage.removeItem(CLIENT_STORAGE_KEY);
    }
  }, []);

  const clearClient = () => {
    setSelectedClientId(null);
    localStorage.removeItem(CLIENT_STORAGE_KEY);
  };

  const value: ClientContextValue = {
    selectedClientId,
    accessibleClients,
    isLoadingClients,
    error,
    selectClient,
    clearClient,
    loadAccessibleClients,
    isAWSUser: isAWSUser(),
    isAdminUser: isAdmin,
    hasClientAccess
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
